import React, { useMemo } from 'react';
import {
  BookOpen,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  GraduationCap,
} from 'lucide-react';
import { useStudentData } from '../../hooks/useStudentData';
import { useCurrency } from '../../contexts/CurrencyContext';
import DataTable from '../ui/DataTable';
import EmptyState from '../ui/EmptyState';
import LoadingSpinner from '../ui/LoadingSpinner';
import MetricCard from '../ui/MetricCard';
import PageHeader from '../ui/PageHeader';
import StatusBadge from '../ui/StatusBadge';
import Surface from '../ui/Surface';

const paymentStatusLabel: Record<string, string> = {
  pending: 'معلق',
  paid: 'مدفوع',
  overdue: 'متأخر',
  cancelled: 'ملغي',
  refunded: 'مسترد',
};

const paymentStatusTone = (status: string) => {
  if (status === 'paid') return 'success';
  if (status === 'overdue') return 'danger';
  if (status === 'pending') return 'warning';
  return 'neutral';
};

const StudentReports: React.FC = () => {
  const { stats, classes, assignments, payments, loading } = useStudentData();
  const { formatAmount } = useCurrency();

  const assignmentRows = useMemo(
    () =>
      assignments.map((assignment: any) => ({
        id: assignment.id,
        title: assignment.title || assignment.name || 'واجب بدون عنوان',
        status: assignment.status || 'active',
        grade: assignment.grade,
        maxGrade: assignment.maxGrade || assignment.totalGrade,
      })),
    [assignments]
  );

  if (loading) {
    return (
      <Surface className='flex min-h-[24rem] items-center justify-center'>
        <LoadingSpinner size='large' />
      </Surface>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        eyebrow='تقارير الطالب'
        title='تقارير الأداء الأكاديمي'
        description='قراءة مركزة للبيانات المتاحة من عضويات الطالب، دون رسوم أو توصيات تجريبية.'
      />

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'>
        <MetricCard
          icon={GraduationCap}
          label='المعدل العام'
          value={stats?.averageGrade ? stats.averageGrade.toFixed(1) : '0.0'}
          tone='green'
        />
        <MetricCard
          icon={BookOpen}
          label='الفصول المسجلة'
          value={stats?.enrolledClasses || 0}
          tone='blue'
        />
        <MetricCard
          icon={CheckCircle2}
          label='الحصص المكتملة'
          value={stats?.completedSessions || 0}
          tone='green'
        />
        <MetricCard
          icon={Clock}
          label='واجبات قيد المتابعة'
          value={stats?.upcomingAssignments || 0}
          tone='amber'
        />
      </div>

      <div className='grid grid-cols-1 gap-6 xl:grid-cols-2'>
        <Surface padding='lg'>
          <div className='mb-4'>
            <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
              الفصول الحالية
            </h2>
            <p className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'>
              مصدرها العضويات النشطة في مساحات المدرسين.
            </p>
          </div>
          <DataTable
            caption='فصول الطالب'
            columns={[
              {
                key: 'class',
                header: 'الفصل',
                cell: row => (
                  <div>
                    <p className='font-semibold text-secondary-950 dark:text-white'>
                      {row.name}
                    </p>
                    <p className='text-xs text-secondary-500 dark:text-secondary-400'>
                      {row.subject}
                    </p>
                  </div>
                ),
              },
              {
                key: 'teacher',
                header: 'المدرس',
                hideOnMobile: true,
                cell: row => row.teacherName,
              },
              {
                key: 'schedule',
                header: 'الموعد',
                hideOnMobile: true,
                cell: row => row.schedule,
              },
            ]}
            rows={classes}
            rowKey={row => row.id}
            emptyState={
              <EmptyState
                icon={BookOpen}
                title='لا توجد فصول'
                description='ستظهر الفصول بعد اعتماد الانضمام من المدرس.'
                compact
              />
            }
          />
        </Surface>

        <Surface padding='lg'>
          <div className='mb-4'>
            <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
              الواجبات والتقييم
            </h2>
            <p className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'>
              آخر حالة معروفة للواجبات القادمة أو المسلمة.
            </p>
          </div>
          <DataTable
            caption='واجبات الطالب في التقارير'
            columns={[
              {
                key: 'title',
                header: 'الواجب',
                cell: row => row.title,
              },
              {
                key: 'status',
                header: 'الحالة',
                cell: row => (
                  <StatusBadge
                    tone={
                      row.status === 'graded'
                        ? 'success'
                        : row.status === 'overdue'
                        ? 'danger'
                        : 'info'
                    }
                  >
                    {row.status === 'graded'
                      ? 'مقيّم'
                      : row.status === 'submitted'
                      ? 'مسلّم'
                      : row.status === 'overdue'
                      ? 'متأخر'
                      : 'نشط'}
                  </StatusBadge>
                ),
              },
              {
                key: 'grade',
                header: 'الدرجة',
                className: 'text-left tabular-nums',
                cell: row =>
                  row.grade !== undefined
                    ? `${row.grade}${row.maxGrade ? ` / ${row.maxGrade}` : ''}`
                    : 'غير متاحة',
              },
            ]}
            rows={assignmentRows}
            rowKey={row => row.id}
            emptyState={
              <EmptyState
                icon={FileText}
                title='لا توجد واجبات في التقرير'
                description='لا توجد واجبات منشورة أو مسجلة لهذا الطالب حاليًا.'
                compact
              />
            }
          />
        </Surface>
      </div>

      <Surface padding='lg'>
        <div className='mb-4 flex items-center gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-950 dark:text-primary-300'>
            <CreditCard className='h-5 w-5' aria-hidden='true' />
          </div>
          <div>
            <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
              ملخص المدفوعات
            </h2>
            <p className='text-sm text-secondary-500 dark:text-secondary-400'>
              هذه سجلات دفتر المدرس، وليست تحصيلًا ماليًا مباشرًا عبر المنصة.
            </p>
          </div>
        </div>
        <DataTable
          caption='مدفوعات الطالب'
          columns={[
            {
              key: 'description',
              header: 'الوصف',
              cell: payment => payment.description || 'دفعة طالب',
            },
            {
              key: 'status',
              header: 'الحالة',
              cell: payment => (
                <StatusBadge tone={paymentStatusTone(payment.status) as any}>
                  {paymentStatusLabel[payment.status] || payment.status}
                </StatusBadge>
              ),
            },
            {
              key: 'amount',
              header: 'المبلغ',
              className: 'text-left tabular-nums',
              cell: payment => formatAmount(payment.amount || 0),
            },
          ]}
          rows={payments}
          rowKey={payment => payment.id}
          emptyState={
            <EmptyState
              icon={CreditCard}
              title='لا توجد مدفوعات'
              description='ستظهر سجلات المدفوعات بعد إضافتها من المدرس.'
              compact
            />
          }
        />
      </Surface>
    </div>
  );
};

export default StudentReports;
